import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ClientProvider } from './context/ClientContext';
import { useAuth, AuthProvider } from './context/AuthContext';
import { CarritoProvider } from './context/CarritoContext';
import { useClientTheme } from './hooks/useClientTheme';
import { DynamicClientComponent } from './components/DynamicClientComponent';
import { ProtectedRoute } from './components/protectedroutes/ProtectedRoute';
import { MainLayout } from './components/Layout/MainLayout';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

import { LoginForm } from './views/Login/LoginForm';
import { Catalogo } from './views/Catalogo/Productos';
import { ProductoDetalle } from './views/Catalogo/ProductoDetalle';
import { Carrito } from './views/Carrito/index';
import { PedidoConfirmation } from './views/Carrito/PedidoConfirmation';
import { MisPedidos } from './views/Pedidos/MisPedidos';
import { DetallePedido } from './views/Pedidos/DetallePedido';
import { PedidoPendiente } from './views/Pedidos/PedidoPendiente';
import { PedidoFallido } from './views/Pedidos/PedidoFallido';
import { PagoPayPal } from './views/Pedidos/PagoPayPal';
import { CheckoutMP } from './views/Checkout/CheckoutMP';
import { CheckoutTransferencia } from './views/Checkout/CheckoutTransferencia';
import { Perfil } from './views/Perfil/Perfil';
import { AdminLayout } from './views/Admin/AdminLayout';
import { Dashboard } from './views/Admin/Dashboard';
import { ConfigLanding } from './views/Admin/ConfigLanding';
import { ConfigTemplate4 } from './views/Admin/ConfigTemplate4';
import { Cobros } from './views/Admin/Cobros';
import { PedidosAdmin } from './views/Admin/PedidosAdmin';
import { PedidoDetalleAdmin } from './views/Admin/PedidoDetalleAdmin';
import { AdminProductos } from './views/Admin/AdminProductos';
import { AdminProductoForm } from './views/Admin/AdminProductoForm';
import { AdminCategorias } from './views/Admin/AdminCategorias';
import { AdminPromociones } from './views/Admin/AdminPromociones';
import { AdminPromocionForm } from './views/Admin/AdminPromocionForm';
import { CuentasTransferencia } from './views/Admin/CuentasTransferencia';
import { Colaboradores } from './views/Admin/Colaboradores';
import { EmailLogs } from './views/Admin/EmailLogs';
import { AdminFaqs } from './views/Admin/AdminFaqs';
import { Margenes } from './views/Admin/Margenes';
import { Faq } from './views/Faq/Faq';
import { Clientes } from './views/Admin/Clientes';
import { ClienteDetalle } from './views/Admin/ClienteDetalle';
import { AdminRoute } from './components/protectedroutes/AdminRoute';
import { VendedorRoute } from './components/protectedroutes/VendedorRoute';
import { RolRoute } from './components/protectedroutes/RolRoute';
import { ReporteTransferencias } from './views/Admin/ReporteTransferencias';
import { VendedorPedido } from './views/Vendedor/VendedorPedido';
import { useClient } from './context/ClientContext';
import { PlatformAuthProvider } from './context/PlatformAuthContext';
import { PlatformRoute } from './components/protectedroutes/PlatformRoute';
import { PlatformLogin } from './views/Platform/PlatformLogin';
import { PlatformLayout } from './views/Platform/PlatformLayout';
import { Localidades } from './views/Platform/Localidades';
import { Dashboard as PlatformDashboard } from './views/Platform/Dashboard';
import { TiendaForm } from './views/Platform/TiendaForm';
import { resolveImgUrl } from './templates/4/utils';
import './App.css';

function ConfigLandingRouter() {
  const { templateId } = useClient();
  return templateId === 4 ? <ConfigTemplate4 /> : <ConfigLanding />;
}

function AppContent() {
  useClientTheme();
  const { clientId, config, loadingConfig, notFound } = useClient();
  const location = useLocation();

  const { token, logout } = useAuth();

  useEffect(() => {
    if (!token || !clientId) return;
    try {
      const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(b64));
      if (payload.tenantId && payload.tenantId !== clientId) {
        logout();
      }
    } catch { }
  }, [clientId, token]);

  useEffect(() => {
    if (!config) return;
    document.title = config.displayName || config.name || 'IP Web';
    const favicon = document.querySelector("link[rel='icon']") ?? Object.assign(document.createElement('link'), { rel: 'icon' });
    favicon.href = config.favicon ? resolveImgUrl(config.favicon) : '/favicon.ico';
    if (!favicon.parentNode) document.head.appendChild(favicon);
  }, [config]);

  // Panel platform (cross-tienda): árbol propio, sin config de tienda ni MainLayout
  const isPlatformPage = location.pathname.startsWith('/platform');
  if (isPlatformPage) {
    return (
      <PlatformAuthProvider>
        <Routes>
          <Route path="/platform/login" element={<PlatformLogin />} />
          <Route path="/platform" element={<PlatformRoute><PlatformLayout><PlatformDashboard /></PlatformLayout></PlatformRoute>} />
          <Route path="/platform/localidades" element={<PlatformRoute><PlatformLayout><Localidades /></PlatformLayout></PlatformRoute>} />
          <Route path="/platform/tiendas/nueva" element={<PlatformRoute><PlatformLayout><TiendaForm /></PlatformLayout></PlatformRoute>} />
          <Route path="/platform/tiendas/:slug" element={<PlatformRoute><PlatformLayout><TiendaForm /></PlatformLayout></PlatformRoute>} />
        </Routes>
      </PlatformAuthProvider>
    );
  }

  if (loadingConfig) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5' }}>
      <div style={{ width: 40, height: 40, border: '4px solid #ddd', borderTopColor: '#333', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (notFound) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, color: '#555' }}>
      <span style={{ fontSize: 48 }}>404</span>
      <span>Tienda no encontrada</span>
    </div>
  );

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const C = isLocalhost ? '/:clientId' : ''; // prefijo de ruta: slug en path (local) o en subdominio (prod)

  // En producción, si el subdominio es reservado (ej. platform) y la ruta no empieza con /platform,
  // redirigir a /platform para evitar que caiga en rutas de tienda
  if (!isLocalhost && !clientId && !location.pathname.startsWith('/platform')) {
    return <Navigate to="/platform" replace />;
  }

  const isLoginPage = location.pathname === '/login' || location.pathname.endsWith('/login');
  const isAdminPage = location.pathname.includes('/admin');

  const routes = (
    <Routes>
      <Route path="/login" element={<LoginForm />} />
      <Route path={`${C}/login`} element={<LoginForm />} />
      <Route path={C || '/'} element={<DynamicClientComponent componentName="Home" />} />
      <Route path={`${C}/catalogo`} element={<Catalogo />} />
      <Route path={`${C}/faq`} element={<Faq />} />
      <Route path={`${C}/catalogo/:id`} element={<ProductoDetalle />} />
      <Route path={`${C}/carrito`} element={<ProtectedRoute><Carrito /></ProtectedRoute>} />
      <Route path={`${C}/checkout/:id`} element={<ProtectedRoute><CheckoutMP /></ProtectedRoute>} />
      <Route path={`${C}/checkout-transferencia`} element={<ProtectedRoute><CheckoutTransferencia /></ProtectedRoute>} />
      <Route path={`${C}/success`} element={<ProtectedRoute><PedidoConfirmation /></ProtectedRoute>} />
      <Route path={`${C}/pedido/exitoso`} element={<ProtectedRoute><PedidoConfirmation /></ProtectedRoute>} />
      <Route path={`${C}/pedido/pendiente`} element={<ProtectedRoute><PedidoPendiente /></ProtectedRoute>} />
      <Route path={`${C}/pedido/fallido`} element={<ProtectedRoute><PedidoFallido /></ProtectedRoute>} />
      <Route path={`${C}/pedido/paypal`} element={<ProtectedRoute><PagoPayPal /></ProtectedRoute>} />
      <Route path={`${C}/mis-pedidos`} element={<ProtectedRoute><MisPedidos /></ProtectedRoute>} />
      <Route path={`${C}/mis-pedidos/:id`} element={<ProtectedRoute><DetallePedido /></ProtectedRoute>} />
      <Route path={`${C}/perfil`} element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
      <Route path={`${C}/admin`} element={<AdminRoute><AdminLayout><Dashboard /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/landing`} element={<AdminRoute><AdminLayout><ConfigLandingRouter /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/cobros`} element={<RolRoute roles={[4]}><AdminLayout><Cobros /></AdminLayout></RolRoute>} />
      <Route path={`${C}/admin/venta`} element={<VendedorRoute><AdminLayout><VendedorPedido /></AdminLayout></VendedorRoute>} />
      <Route path={`${C}/admin/pedidos`} element={<VendedorRoute><AdminLayout><PedidosAdmin /></AdminLayout></VendedorRoute>} />
      <Route path={`${C}/admin/pedidos/:id`} element={<VendedorRoute><AdminLayout><PedidoDetalleAdmin /></AdminLayout></VendedorRoute>} />
      <Route path={`${C}/admin/productos`} element={<AdminRoute><AdminLayout><AdminProductos /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/productos/:id`} element={<AdminRoute><AdminLayout><AdminProductoForm /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/categorias`} element={<AdminRoute><AdminLayout><AdminCategorias /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/promociones`} element={<AdminRoute><AdminLayout><AdminPromociones /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/promociones/:id`} element={<AdminRoute><AdminLayout><AdminPromocionForm /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/cuentas`} element={<RolRoute roles={[4]}><AdminLayout><CuentasTransferencia /></AdminLayout></RolRoute>} />
      <Route path={`${C}/admin/transferencias`} element={<RolRoute roles={[4]}><AdminLayout><ReporteTransferencias /></AdminLayout></RolRoute>} />
      <Route path={`${C}/admin/clientes`} element={<AdminRoute><AdminLayout><Clientes /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/clientes/:id`} element={<AdminRoute><AdminLayout><ClienteDetalle /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/emails`} element={<AdminRoute><AdminLayout><EmailLogs /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/faqs`} element={<AdminRoute><AdminLayout><AdminFaqs /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/margenes`} element={<AdminRoute><AdminLayout><Margenes /></AdminLayout></AdminRoute>} />
      <Route path={`${C}/admin/colaboradores`} element={<AdminRoute><AdminLayout><Colaboradores /></AdminLayout></AdminRoute>} />
      {isLocalhost && <Route path="/" element={<Navigate to={clientId ? `/${clientId}` : '/platform'} replace />} />}
      <Route path="*" element={<Navigate to={clientId ? (isLocalhost ? `/${clientId}` : '/') : '/platform'} replace />} />
    </Routes>
  );

  if (isLoginPage || isAdminPage) return routes;

  return (
    <MainLayout>
      {routes}
    </MainLayout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ClientProvider>
        <AuthProvider>
          <CarritoProvider>
            <AppContent />
          </CarritoProvider>
        </AuthProvider>
      </ClientProvider>
    </BrowserRouter>
  );
}

export default App;